import { createFileRoute } from "@tanstack/react-router";
import { formatDistanceToNow } from "date-fns";
import { toast } from "sonner";
import { workspaceSlug } from "../lib/hosts";
import { useWorkspace } from "../lib/workspaces";
import { useDeletedProjects, useRestoreProject } from "../lib/projects";
import { Card, CardContent, CardDescription, CardHeader } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { strings } from "../lib/strings";
import { ApiError } from "../lib/api";
import { ToWorkspaces } from "../components/workspaces/ToWorkspaces";
import { AdministratorsOnly, Unavailable } from "../components/ui/states";

const words = strings.projects.trash;

/**
 * The workspace's trash, for Steer's Administrators (BUILD_SPEC §26.1): the projects deleted in the last 30 days, each
 * restorable as it was with its issues; after that they are deleted for good. A project's own trash of issues is in its
 * settings.
 */
function TrashRoute() {
  const slug = workspaceSlug();
  const view = useWorkspace(slug);
  const administrator = view.data?.role === "administrator";
  const deleted = useDeletedProjects(slug, administrator);
  const restore = useRestoreProject(slug);

  if (view.isLoading)
    return (
      <div className="mx-auto max-w-3xl space-y-3 py-12">
        <div className="h-8 w-48 animate-pulse rounded bg-[hsl(var(--muted))]" />
        <div className="h-40 animate-pulse rounded bg-[hsl(var(--muted))]" />
      </div>
    );
  if (view.error instanceof ApiError || !view.data) return <ToWorkspaces />;
  if (!administrator) return <AdministratorsOnly what={strings.states.adminPages.trash} />;
  const projects = deleted.data ?? [];

  return (
    <div className="mx-auto max-w-3xl space-y-6 py-12">
      <Card>
        <CardHeader>
          <h1 className="text-xl leading-none font-semibold tracking-tight">{words.title}</h1>
          <CardDescription>{words.body}</CardDescription>
        </CardHeader>
        <CardContent>
          {deleted.isLoading ? (
            <div className="h-16 animate-pulse rounded bg-[hsl(var(--muted))]" />
          ) : deleted.data === undefined ? (
            <Unavailable what={words.theTrash} onRetry={() => void deleted.refetch()} />
          ) : projects.length === 0 ? (
            <p className="text-sm text-[hsl(var(--muted-foreground))]">{words.empty}</p>
          ) : (
            <ul className="divide-y divide-[hsl(var(--border))]">
              {projects.map((project) => (
                <li key={project.id} className="flex items-center gap-2 py-2 text-sm">
                  <span className="rounded bg-[hsl(var(--muted))] px-1.5 py-0.5 font-mono text-xs">{project.key}</span>
                  <span className="min-w-0 flex-1 truncate">{project.name}</span>
                  <span className="text-xs text-[hsl(var(--muted-foreground))]">
                    {words.deletedAgo.replace(
                      "{when}",
                      formatDistanceToNow(project.deletedAt ?? 0, { addSuffix: true }),
                    )}
                  </span>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={restore.isPending}
                    onClick={() =>
                      restore.mutate(project.id, {
                        onSuccess: () => toast.success(words.restored.replace("{name}", project.name)),
                        onError: (err) =>
                          toast.error(err instanceof ApiError && err.status < 500 ? err.message : words.restoreFailed),
                      })
                    }
                  >
                    {words.restore}
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export const Route = createFileRoute("/admin/settings/trash")({ component: TrashRoute });
